<template>
  <div class="mfa-recovery">
    <div class="page-header">
      <div>
        <p class="eyebrow">SECURITY</p>
        <h1>2要素認証リセット</h1>
        <p>
          誰が2要素認証を登録しているかを確認し、
          <b>端末を失くした社員の登録を外して、やり直してもらえます</b>。
        </p>
      </div>
    </div>

    <!--
      上端に出すのは読み込みの失敗だけ。外した結果は押した人の行に、
      断られた理由は確認の小窓の中に出す（ここに出すと、開いたままの
      小窓の暗幕の下に隠れて読めなかった）。
    -->
    <CAlert
      v-if="errorMessage"
      color="danger"
      close-button
      @update:show="errorMessage = ''"
    >{{ errorMessage }}</CAlert>

    <div v-if="loading" class="mfa-recovery__loading">
      <CSpinner color="primary"/>
      <span>登録状況を読み込んでいます。</span>
    </div>

    <template v-else>
      <!--
        まず数を見せる。

        **強制を始める前に、何人が止まるかを知る。**Google は展開手順の
        中に「登録状況を追う」を1ステップとして持っているし、GitHub は
        必須化の前に確認できるようにしてある。数を知らずに強制すると、
        当日に何人止まるか分からない。
      -->
      <!--
        **いま何が起きているかを先に言う。**

        2026-08-27 まで、この画面は「必須にすると〜」という仮定の文しか
        出していなかった。開いた人は、フラグを立てたのに何も変わらないのを見て
        「いま必須なのかどうかが分からない」と言った。当然で、書いていない。
      -->
      <CCard v-if="stage" class="mfa-recovery__stage-card">
        <CCardBody>
          <p :class="['mfa-recovery__stage', stageClass]">
            {{ stageHeadline }}
          </p>
          <p class="mfa-recovery__stage-detail">{{ stageDetail }}</p>
        </CCardBody>
      </CCard>

      <CCard class="mfa-recovery__summary">
        <CCardBody>
          <div class="mfa-recovery__counts">
            <div>
              <span class="mfa-recovery__count">{{ status.total }}</span>
              <span class="mfa-recovery__label">利用者</span>
            </div>
            <div :class="{ 'mfa-recovery__warn': status.notEnrolled > 0 }">
              <span class="mfa-recovery__count">{{ status.notEnrolled }}</span>
              <span class="mfa-recovery__label">まだ登録していない</span>
            </div>
          </div>
          <!--
            **止まるのは管理者だけ。**必須にしても、一般・担当者は
            今までどおり入れる（AdminMfaEnforcement.stageFor が管理者以外を
            必ず NONE で返す）。

            ここで全員を数えていた。25人中24人が未登録の会社で「24人」と
            出たが、実際に止まるのは4人だった。**6倍の数字を見せられたら、
            誰も必須にしない。**
          -->
          <!--
            **どの行かを指す**（見直しの指摘、2026-09-30）。ここで言う
            「管理者」は権限グループの名前では決まらないので、下の一覧の
            印で探せるように言う。
          -->
          <!--
            文の途中で改行しない。テンプレートの改行は空白になり、画面では
            「管理者では ないので」と途中に隙間が出ていた（2026-09-30 に画面で見た）。
          -->
          <p v-if="status.adminsNotEnrolled > 0" class="mfa-recovery__note">
            管理者の2要素認証を必須にすると、<b>この{{ status.adminsNotEnrolled }}人の管理者が、次のログインで登録を求められます</b>。
            <span class="mfa-recovery__aside mfa-recovery__pointer">
              対象者は、一覧で「{{ TARGET_BADGE }}」と「未登録」の両方が表示されている方です。
            </span>
            <span v-if="othersNotEnrolled > 0" class="mfa-recovery__aside">
              （ほかに{{ othersNotEnrolled }}人が未登録ですが、管理者ではないので求められません）
            </span>
          </p>
          <p v-else-if="status.notEnrolled > 0" class="mfa-recovery__note">
            <b>管理者は全員が登録済みです。</b>必須にしても、いま止まる人はいません。（管理者以外に{{ status.notEnrolled }}人の未登録がいますが、求められません）
          </p>
          <p v-else class="mfa-recovery__note">
            全員が登録済みです。
          </p>
        </CCardBody>
      </CCard>

      <CCard>
        <CCardHeader>
          <strong>利用者ごとの状況</strong>
          <p>まだ登録していない人が上に出ます。</p>
          <p class="mfa-recovery__legend">
            「{{ TARGET_BADGE }}」の印は、管理者の2要素認証を必須にした際に対象となる利用者に表示されます。未登録の場合、お願い期間中または必須化後の初回ログイン時に登録を求められます。
          </p>
        </CCardHeader>

        <CCardBody>
          <p v-if="!status.users.length" class="mfa-recovery__empty">
            利用者がいません。
          </p>

          <ul v-else class="mfa-recovery__list">
            <li
              v-for="user in status.users"
              :key="user.userId"
              class="mfa-recovery__user"
            >
              <div class="mfa-recovery__body">
                <p class="mfa-recovery__name">
                  {{ user.userName }}
                  <!--
                    **札はその人の権限グループの名前**（束22）。以前は旧3区分
                    （管理者／担当者／一般）で、独自の権限グループの人は
                    「一般」と出ていた。名前が無い（古いサーバ）ときだけ旧区分。
                  -->
                  <span class="mfa-recovery__badge" :title="badgeOf(user)">
                    {{ badgeOf(user) }}
                  </span>
                  <!--
                    **必須にしたとき止まる管理者の印**（見直しの指摘、2026-09-30）。
                    止まる「管理者」は権限グループの名前ではなく、サーバが止める
                    ときと同じ判定で決まる。札からは読めないので、サーバが行ごとに
                    返す印（adminMfaTarget）で出す。上の人数もこの印で数えている。
                  -->
                  <span
                    v-if="user.adminMfaTarget === true"
                    class="mfa-recovery__badge mfa-recovery__badge--target"
                    :title="TARGET_BADGE_TITLE"
                  >{{ TARGET_BADGE }}</span>
                  <span
                    v-if="user.enrolled"
                    class="mfa-recovery__badge mfa-recovery__badge--ok"
                  >登録済み</span>
                  <span
                    v-else
                    class="mfa-recovery__badge mfa-recovery__badge--todo"
                  >未登録</span>
                </p>
                <p class="mfa-recovery__detail">
                  社員番号: {{ user.employeeId || '不明' }}
                  <template v-if="user.skippedCount > 0">
                    ／ 「あとで」を {{ user.skippedCount }} 回
                  </template>
                </p>
                <p
                  v-if="user.enrolled && blockedReason(user)"
                  class="mfa-recovery__blocked"
                >{{ blockedReason(user) }}</p>
                <ActionResultMessage :text="rowMessages[user.userId] || ''" />
              </div>

              <!--
                登録していない人に「外す」ボタンを出さない。

                **押しても何も起きない。**外すものが無い。押せるボタンを
                出すと、押した人は「効かなかった」と思う。

                **押すと必ず断られる行にも出さない**（束22）。自分自身の行と、
                社内システム管理者から見た契約企業管理者の行。押せるかは
                サーバが実際に外すときと同じ決まりで答える（canReset）。
                答えが無い（古いサーバ）ときは、これまでどおり出す。
              -->
              <CButton
                v-if="user.enrolled && user.canReset !== false"
                color="outline-danger"
                size="sm"
                :disabled="busy"
                @click="startReset(user)"
              >2要素認証を外す</CButton>
            </li>
          </ul>
        </CCardBody>
      </CCard>
    </template>

    <!--
      外す前に確かめる。

      **取り返しがつかない。**外された人の認証アプリと予備のコードは
      使えなくなる。誰を外すのかを、押す前にもう一度見せる。
    -->
    <CModal
      :show="Boolean(resetTarget)"
      title="2要素認証を解除します"
      color="danger"
      @update:show="cancelReset"
    >
      <p v-if="resetTarget">
        <b>{{ resetTarget.userName }}</b> さんの2要素認証を解除します。
      </p>
      <!--
        **外したあとに起きることを、実際どおりに書く**（束22）。

        以前は「次のログインで登録をやり直します」と書いていた。外す処理は
        認証アプリと予備のコードを消すだけで、次のログインで登録を求める
        のは、会社が管理者の2要素認証を必須（か登録のお願い）にしていて、
        その人が管理者のときだけ。一般社員は2要素認証なしで入れるように
        なる。

        **条件で言わず、その行の印と、いまの段で言い切る**（見直しの指摘、
        2026-09-30）。「この方が管理者なら」と書いても、押した人には判断する
        材料が無かった。しかも予告だけの段では、外された管理者は登録を
        求められずに2要素なしで入れる（手元の back で確かめた）のに、
        「登録を求められます」と読める文だった。
      -->
      <p>
        解除すると、その方の認証アプリの登録と予備のコードはすべて削除されます。
      </p>
      <p class="mfa-recovery__consequence">{{ resetConsequence }}</p>
      <!--
        **理由は必須**（2026-10-11、W-5）。欄は「記録に残ります」と言うのに、空のまま
        外せて、理由の無い記録が残っていた。必須の印は、招待の理由の欄と同じ形
        （欄の上の見出しに * ）。空のまま［外す］を押したら、送らずに小窓の中で断る。
        サーバも空を断る。
      -->
      <label for="mfa-reset-reason">理由（記録に残ります）<span class="asterisk">*</span></label>
      <CInput
        id="mfa-reset-reason"
        v-model="resetReason"
        placeholder="例: 端末を紛失、機種変更"
        maxlength="200"
      />
      <ActionResultMessage :text="resetError" type="danger" />
      <template #footer>
        <CButton color="secondary" :disabled="busy" @click="cancelReset">
          やめる
        </CButton>
        <CButton color="danger" :disabled="busy" @click="confirmReset">
          外す
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import {
  getEnrollmentStatus,
  resetMfaFor,
  serverMessage,
  isNetworkFailure,
  isReauthenticationRequired
} from '../../services/mfaAdminRecovery'
import ActionResultMessage from '../../components/ActionResultMessage.vue'
import { resetReasonToSend } from './mfaResetReason'

/**
 * 権限名の見せ方。
 *
 * **知らない値はそのまま出さない。** サーバが増やした値をそのまま出すと、
 * 英字のまま画面へ並ぶ。
 */
/**
 * いま何が起きているか。
 *
 * <b>サーバの段（AdminMfaStage）をそのまま出さない。</b>NONE / ANNOUNCED /
 * PROMPTED / ENFORCED と英字で並べても、開いた人には読めない。
 *
 * 見出しは<b>いま止まるかどうか</b>を先に言う。開いた人がいちばん
 * 知りたいのはそこで、段の名前ではない。
 */
const STAGE_LABELS = {
  NONE: {
    headline: 'いまは必須ではありません',
    detail: '管理者の2要素認証は必須化されていません。設定は任意です。',
    tone: 'off'
  },
  ANNOUNCED: {
    headline: 'いまは必須ではありません（予告だけ出しています）',
    detail: '管理者のログイン後に案内が出ますが、まだ止まりません。',
    tone: 'soon'
  },
  PROMPTED: {
    headline: 'いまは必須ではありません（登録をお願いしています）',
    detail: '管理者のログイン後に登録画面が出ますが、「あとで」で先へ進めます。押せる回数には上限があります。',
    tone: 'soon'
  },
  ENFORCED: {
    headline: '必須設定です',
    detail: '登録していない管理者は、登録するまで先へ進めません。',
    tone: 'on'
  }
}

const AUTHORITY_LABELS = {
  admin: '管理者',
  clerk: '担当者',
  common: '一般'
}

/*
 * 必須にしたとき止まる管理者の印と、その説明（見直しの指摘、2026-09-30）。
 * 札の隣・一覧の見出しの説明・確認の小窓で同じ呼び名を使うので、1か所に置く。
 */
const TARGET_BADGE = '必須対象'
const TARGET_BADGE_TITLE =
  '管理者の2要素認証を必須にした際に対象となる利用者です。'

/**
 * 外したあと、その方がどうなるか（確認の小窓の2段落目）。
 *
 * <b>その行の印（adminMfaTarget）と、いまの段で決める。</b>外すと「あとで」を
 * 押した回数も0に戻るので、外された方の段は、この画面の段（まだ登録していない
 * 管理者から見た段）と同じになる。
 *
 * 段か印が分からないときは言い切らない。<b>分からないことを「入れます」とも
 * 「止まります」とも言わない。</b>
 */
/*
 * 文は Gemini が決めたもの（2026-10-08、LM-20〜LM-26）。印の名前は、同じ画面の
 * ほかの文（人数の添え書き・一覧の説明）と同じく「 」で囲んで呼ぶ。
 */
const FREE_AFTER_RESET =
  '本人が「セキュリティ設定」で登録し直すまで、確認コードなしでログインできる状態になります。'

const RESET_CONSEQUENCES = {
  unknown: '設定情報を取得できなかったため、次のログイン時に登録が求められるかは不明です。'
    + '解除後は、本人の「セキュリティ設定」から設定し直すようお伝えください。',
  notTarget: 'この方は管理者の2要素認証の対象外のため、登録は求められません。'
    + FREE_AFTER_RESET,
  ENFORCED: '「' + TARGET_BADGE + '」が付いているため、対象の方は次のログイン時に登録画面が表示され、'
    + '登録を完了するまでログインできません。',
  PROMPTED: '「' + TARGET_BADGE + '」が付いているため、対象の方は次のログイン時に登録画面が表示されます。'
    + 'ただし、［あとで登録する］を押せば登録せずに進むことができます。',
  ANNOUNCED: '「' + TARGET_BADGE + '」が付いていますが、現在は予告期間のため、次のログイン時に登録は求められません。'
    + FREE_AFTER_RESET,
  /*
   * NONE は「必須にしていない」だけでなく「必須にしたが、予告を始める日より
   * 前」も含む。上の段の札は「いまは必須ではありません」。
   */
  NONE: '「' + TARGET_BADGE + '」が付いていますが、現在は必須ではないため、登録は求められません。'
    + FREE_AFTER_RESET
}

/*
 * 理由が空か、200文字を超えたまま［外す］を押したときの文（2026-10-11、W-5）。
 *
 * 文は Gemini が決めたもの（2026-10-11、M1）。言い回しを変えない。サーバの側
 * （MfaAdminRecoveryService の REFUSED_REASON_MISSING）も同じ文。
 */
const RESET_REASON_REQUIRED = '理由は1文字以上200文字以内で入力してください。'
/* 送る理由を作る決まり（前後の空白・1〜200文字・制御文字）は mfaResetReason.js。サーバと同じ決まり。 */

/*
 * **一時パスを出すボタンは、ここには置かない（場面 S-95）。**
 *
 * ここは顧客企業の管理者の画面。2要素を<b>外す</b>のはそれでよい
 * ——外された人は次のログインで自分で登録し直せる。
 *
 * 一時パスを<b>出す</b>のは違う。<b>会社側の管理者が、自分の第2要素を
 * 迂回する資格情報を自分で作れる</b>ことになる。出す画面は運営側
 * （views/platform/PlatformContractManagement.vue の「一時パス」タブ）。
 */
export default {
  name: 'MfaRecovery',

  components: { ActionResultMessage },

  data () {
    return {
      status: {
        users: [], total: 0, notEnrolled: 0, adminsNotEnrolled: 0,
        stage: '', requiredFrom: null
      },
      loading: true,
      busy: false,
      // 読み込みの失敗だけ（画面の上端に出す）。
      errorMessage: '',
      // 外した結果。押した人の行に出す（キーは userId）。
      rowMessages: {},
      // 外すのを断られた理由。確認の小窓の中に出す。
      resetError: '',
      resetTarget: null,
      resetReason: ''
    }
  },

  mounted () {
    this.reload()
  },

  computed: {
    /**
     * いまの段。
     *
     * <b>読めなかったときは空。</b>サーバは方針を読めなければ段を返さない。
     * そこを「必須ではありません」と言い換えると、**分からないことを
     * 大丈夫だと言ったことになる。**空なら、この札そのものを出さない。
     */
    stage () {
      return STAGE_LABELS[this.status.stage] || null
    },

    stageHeadline () {
      return this.stage ? this.stage.headline : ''
    },

    stageDetail () {
      if (!this.stage) return ''
      const from = this.status.requiredFrom
      if (this.status.stage === 'ENFORCED' || !from) {
        return this.stage.detail
      }
      return this.stage.detail
        + '必須になるのは ' + this.asDay(from) + ' 〜'
    },

    stageClass () {
      return this.stage
        ? 'mfa-recovery__stage--' + this.stage.tone
        : ''
    },

    TARGET_BADGE () {
      return TARGET_BADGE
    },

    TARGET_BADGE_TITLE () {
      return TARGET_BADGE_TITLE
    },

    /** 外したあと、その方がどうなるか。印と段で言い切る。 */
    resetConsequence () {
      const target = this.resetTarget
      if (!target) return ''
      const stage = this.status.stage
      if (!STAGE_LABELS[stage] || typeof target.adminMfaTarget !== 'boolean') {
        return RESET_CONSEQUENCES.unknown
      }
      if (!target.adminMfaTarget) return RESET_CONSEQUENCES.notTarget
      return RESET_CONSEQUENCES[stage]
    },

    /** 未登録のうち、管理者ではない人。**求められない人。** */
    othersNotEnrolled () {
      const all = Number(this.status.notEnrolled) || 0
      const admins = Number(this.status.adminsNotEnrolled) || 0
      return Math.max(0, all - admins)
    }
  },

  methods: {
    /** 日付だけにする。時刻は要らない。 */
    asDay (value) {
      const text = String(value || '')
      const day = text.slice(0, 10)
      return day.length === 10 ? day : text
    },

    /**
     * @param quiet 外したあとの読み直しでは true。
     *   **一覧を作り直さない。**loading を立てると一覧が消えて作り直され、
     *   押した人の行に出した結果の文も一緒に消える。
     */
    async reload (quiet) {
      if (!quiet) this.loading = true
      try {
        this.status = await getEnrollmentStatus()
      } catch (error) {
        if (!quiet) this.status = { users: [], total: 0, notEnrolled: 0 }
        this.errorMessage = this.explain(
          error, '登録状況を読み込めませんでした。')
      } finally {
        this.loading = false
      }
    },

    startReset (user) {
      this.resetTarget = user
      this.resetReason = ''
      this.resetError = ''
      this.$delete(this.rowMessages, user.userId)
    },

    cancelReset () {
      if (this.busy) return
      this.resetTarget = null
      this.resetReason = ''
      this.resetError = ''
    },

    async confirmReset () {
      if (this.busy || !this.resetTarget) return

      // 送る理由を1回だけ作る。受け取れない理由（空・200文字超・制御文字入り）なら、送らずに小窓の中で断る。
      const reasonToSend = resetReasonToSend(this.resetReason)
      if (reasonToSend === null) {
        this.resetError = RESET_REASON_REQUIRED
        return
      }

      const target = this.resetTarget
      this.busy = true
      this.resetError = ''
      try {
        // 検査した文字列そのものを送る（入力欄の生の値ではない）。
        await resetMfaFor(target.userId, reasonToSend)
        this.resetTarget = null
        this.resetReason = ''
        // 読み直すと「まだ登録していない人」が上に並び替わる。並んだあとの
        // 行へ出すので、文は読み直してから入れる。
        await this.reload(true)
        this.$set(this.rowMessages, target.userId, target.userName
          + ' さんの2要素認証を外しました。'
          + '管理者の2要素認証が必須（または登録のお願い）の対象者は次のログイン時に登録を求められます。'
          + 'それ以外の方は、必要に応じて本人が「セキュリティ設定」画面から再登録を行ってください。')
      } catch (error) {
        // 小窓は開いたまま（resetTarget を残す）。理由は小窓の中に出す。
        this.resetError = this.explain(
          error, '2要素認証を解除できませんでした。')
      } finally {
        this.busy = false
      }
    },

    /**
     * 断られた理由の見せ方。
     *
     * **サーバの文言をそのまま出す。** サーバは「自分で自分は外せません」
     * 「その利用者が見つかりません」を区別して返す。こちらで1種類に
     * まとめると、押した人は何を直せばよいか分からない。
     *
     * 本人確認のやり直しと通信断だけは、こちらで言い換える。
     * サーバの文言だけでは、次に何をすればよいか分からないため。
     */
    explain (error, fallback) {
      if (isReauthenticationRequired(error)) {
        return 'もう一度ログインの確認が必要です。'
          + ' 一度ログアウトして再ログインしてから、もう一度お試しください。'
      }
      if (isNetworkFailure(error)) {
        return '通信できませんでした。接続を確認してからお試しください。'
      }
      return serverMessage(error) || fallback
    },

    describeAuthority (authorityName) {
      return AUTHORITY_LABELS[authorityName] || 'その他'
    },

    /** 札。権限グループの名前（無ければ旧区分）。 */
    badgeOf (user) {
      const names = typeof user.roleNames === 'string' ? user.roleNames.trim() : ''
      return names || this.describeAuthority(user.authorityName)
    },

    /**
     * ［外す］が無い理由（束22）。サーバが決めた理由の印だけを言葉にする。
     * 印が無ければ何も言わない（ボタンの有無だけで分かる形にしない）。
     */
    blockedReason (user) {
      if (user.canReset !== false) return ''
      if (user.resetBlockedBy === 'SELF') {
        return 'ご自身の2要素認証は「セキュリティ設定」画面で変更してください。'
      }
      if (user.resetBlockedBy === 'OWNER_ONLY') {
        return '契約企業管理者（自社最上位）の2要素認証を解除できるのは、契約企業管理者（自社最上位）のみです。'
      }
      return 'この利用者の2要素認証は解除できません。'
    }
  }
}
</script>

<style scoped>
/*
  2026-08-27 まで、この画面には style が1行も無かった。
  クラス名だけ書いて、定義したつもりで入っていなかった。

  **書いた気になっていた形の典型。**利用者からは
  「なんかぐちゃぐちゃしてみにくい。外すボタンも何に対してかこれだと
  わからない」と言われた。名前と権限と状態が全部つながって1行になり、
  社員番号だけが別の行に並んでいた。
*/

.mfa-recovery__loading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6c757d;
}

/* ---- いま何が起きているか ---- */

.mfa-recovery__stage-card {
  border-left: 4px solid #d8dbe0;
}

.mfa-recovery__stage {
  margin: 0 0 0.25rem;
  font-size: 1.05rem;
  font-weight: 700;
}

.mfa-recovery__stage--off {
  color: #768192;
}

.mfa-recovery__stage--soon {
  color: #f9b115;
}

.mfa-recovery__stage--on {
  color: #2eb85c;
}

.mfa-recovery__stage-detail {
  margin: 0;
  color: #768192;
  font-size: 0.875rem;
}

/* ---- 数 ---- */

.mfa-recovery__counts {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  margin-bottom: 0.75rem;
}

.mfa-recovery__count {
  display: block;
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.2;
  /* 数字の幅をそろえる。並べたときに桁がずれない。 */
  font-variant-numeric: tabular-nums;
}

.mfa-recovery__label {
  display: block;
  font-size: 0.8125rem;
  color: #768192;
}

.mfa-recovery__warn .mfa-recovery__count {
  color: #f9b115;
}

.mfa-recovery__note {
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.7;
}

.mfa-recovery__aside {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.8125rem;
  color: #768192;
}

/* ---- 利用者ごとの状況 ---- */

.mfa-recovery__empty {
  margin: 0;
  color: #768192;
}

.mfa-recovery__list {
  margin: 0;
  padding: 0;
  list-style: none;
}

/*
  **1人を1行にする。**ここが無かったので、25人ぶんの名前・権限・状態が
  切れ目なく1つの段落になっていた。「外す」ボタンが誰のものかも
  分からなくなる。
*/
.mfa-recovery__user {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid #ebedef;
}

.mfa-recovery__user:last-child {
  border-bottom: 0;
}

.mfa-recovery__body {
  min-width: 0;
}

.mfa-recovery__name {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0 0 0.125rem;
  font-weight: 600;
}

.mfa-recovery__detail {
  margin: 0;
  font-size: 0.8125rem;
  color: #768192;
}

/* ［外す］が無い行の理由。ボタンの場所に置かず、名前の下に小さく。 */
.mfa-recovery__blocked {
  margin: 0.125rem 0 0;
  font-size: 0.8125rem;
  color: #768192;
}

.mfa-recovery__badge {
  display: inline-block;
  padding: 0.125rem 0.5rem;
  border-radius: 1rem;
  background: #ebedef;
  color: #4f5d73;
  font-size: 0.75rem;
  font-weight: 600;
  /* 権限や状態は、名前と同じ太さで並ばせない。 */
  white-space: nowrap;
}

.mfa-recovery__badge--ok {
  background: #e6f6ec;
  color: #1b7a41;
}

.mfa-recovery__badge--todo {
  background: #fdf3e0;
  color: #8a5b00;
}

/* 必須にしたとき止まる管理者の印。状態（登録済み・未登録）の色と混ぜない。 */
.mfa-recovery__badge--target {
  background: #fff;
  /* 枠線にすると高さが2px増えて、隣の札と上端がずれる。内側の影で描く。 */
  box-shadow: inset 0 0 0 1px #b0b8c4;
  color: #3c4b64;
}

/* 印の説明。一覧の見出しの下に小さく。 */
.mfa-recovery__legend {
  margin: 0.25rem 0 0;
  font-size: 0.8125rem;
  color: #768192;
}

/*
  狭い画面では縦に積む。**ボタンを名前の下へ置く。**
  横に並べたままだと、名前が潰れて誰のボタンか読めなくなる。
*/
@media (max-width: 575.98px) {
  .mfa-recovery__user {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.5rem;
  }

  .mfa-recovery__counts {
    gap: 1.25rem;
  }
}
</style>
